import moment from "moment";
import { getPatientByIdApi } from "@/api/patientList";
import { transSericeApolloClient } from "@/graphql/client";
import { GET_EVENT_BY_EVENT_ID, GET_TASK } from "@/graphql/queries/my-calendar";
import { GET_PATIENT_ORDER } from "@/graphql/queries/my-patients";
import transServiceV2 from "@/services/transServiceV2";
import { CARD_TYPES } from "./actionCardUtils";
import store from "@/store";

/**
 * Fetch patient info by patient ID
 * Checks store first, then fetches from API if not found
 * Saves to store after fetching from API
 * @param {string|number} patientId - The patient ID to look up
 * @param {object} store - Vuex store instance
 * @returns {Promise<object|null>} - Patient object or null if not found
 */
export const getPatientById = async (patientId, store) => {
  if (!patientId) return null;

  // Check store first
  const backendusers = store.state.myPatients?.patientInfo?.backendusers || {};
  const patientFromStore = backendusers[patientId];

  if (patientFromStore) {
    return patientFromStore;
  }

  // Fetch from API if not in store
  try {
    const patient = await getPatientByIdApi(patientId);

    // Save to store for future use (page=0 for search results)
    if (patient) {
      store.commit("myPatients/patientInfo/ADD_BACKEND_USERS", {
        users: [patient],
      });
    }

    return patient;
  } catch (error) {
    console.error("Failed to fetch patient:", error);
    return null;
  }
};

/**
 * Get patient full name from patient object
 * @param {object} patient - Patient object
 * @returns {string} - Full name or empty string
 */
export const getPatientFullName = (patient) => {
  if (!patient) return "";
  const firstName = patient.patient_first_name || "";
  const lastName = patient.patient_last_name || "";
  return `${firstName} ${lastName}`.trim();
};

export const normalizeNotesResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.notes)) return response.notes;
  if (Array.isArray(response?.encounter_notes)) return response.encounter_notes;
  return [];
};

export const normalizeNotesListResponse = (response) => {
  const notes = normalizeNotesResponse(response);
  const total = response?.total ?? response?.count ?? notes.length;
  return { notes, total };
};

export const sortNotesByServiceDateDesc = (notes) => {
  return [...notes].sort((a, b) => {
    const dateA = a.service_date ? moment(a.service_date).valueOf() : 0;
    const dateB = b.service_date ? moment(b.service_date).valueOf() : 0;
    return dateB - dateA;
  });
};

export const mapPastNotes = (notes) => {
  return notes.map((note) => ({
    id: note._id || note.id,
    patient_id: note.patient_id,
    date: note.service_date ? moment(note.service_date).format("MMM-DD-YYYY") : "",
    title: note.title || "Untitled Note",
    content: note.content || "",
    created_by: note.created_by,
  }));
};

export const formatEncounterDate = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  const month = months[date.getMonth()];
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${month}-${day}-${year} ${hours}:${minutes} ${ampm}`;
};

/**
 * Fetch event/appointment details by event ID
 * Uses the direct getEventByEventId query for efficient lookup
 * @param {number|string} eventId - The event ID to look up
 * @param {number|string} _calendarId - Unused, kept for backward compatibility with existing callers
 * @returns {Promise<object|null>} - Event object or null if not found
 */
export const getEventById = async (eventId, _calendarId) => {
  if (!eventId) return null;

  try {
    const { data } = await transSericeApolloClient.query({
      query: GET_EVENT_BY_EVENT_ID,
      variables: {
        eventId: parseInt(eventId, 10)
      },
      fetchPolicy: 'network-only',
      context: { _silent: true }
    });

    return data?.getEventByEventId || null;
  } catch (error) {
    const isNotFound = error.graphQLErrors?.some(
      e => e.message?.toLowerCase().includes('not found')
    );
    if (isNotFound) {
      return null;
    }
    console.error("Failed to fetch event:", error);
    return null;
  }
};

/**
 * Format appointment description from event data
 * @param {object} event - Event object from API
 * @returns {string} - Formatted description
 */
export const formatAppointmentDescription = (event) => {
  if (!event) return '';
  
  // Calculate duration in minutes
  const startTime = new Date(event.start_time);
  const endTime = new Date(event.end_time);
  const durationMinutes = Math.round((endTime - startTime) / (1000 * 60));
  
  // Format appointment time: "HH:MM, MM-DD-YYYY"
  const hours = startTime.getHours().toString().padStart(2, '0');
  const minutes = startTime.getMinutes().toString().padStart(2, '0');
  const month = (startTime.getMonth() + 1).toString().padStart(2, '0');
  const day = startTime.getDate().toString().padStart(2, '0');
  const year = startTime.getFullYear();
  const formattedTime = `${hours}:${minutes}, ${month}-${day}-${year}`;
  
  // Build description: "{event_type}, {duration} min | Appointment Time: {formatted_time}"
  const eventType = event.event_type || 'Appointment';
  return `${eventType}, ${durationMinutes} min | Appointment Time: ${formattedTime}`;
};

/**
 * Fetch task details by task ID
 * @param {number|string} taskId - The task ID to look up
 * @returns {Promise<object|null>} - Task object, null if not found, or error object with notFound flag
 */
export const getTaskById = async (taskId) => {
  if (!taskId) return null;

  try {
    const { data } = await transSericeApolloClient.query({
      query: GET_TASK,
      variables: { id: parseInt(taskId, 10) },
      fetchPolicy: 'network-only',
      context: { _silent: true }
    });

    return data?.task || null;
  } catch (error) {
    // Check if task not found (expected case, don't log)
    const isNotFound = error.graphQLErrors?.some(
      e => e.message?.includes('Task not found')
    );

    if (isNotFound) {
      return { _notFound: true };
    }

    console.error("Failed to fetch task:", error);
    return null;
  }
};

// Order status labels and colors from GraphQL order_status_list
const ORDER_STATUS_CONFIG = {
  order_received:        { label: 'New Order',             color: '#43D4C3', bgColor: '#D5EFEE' },
  order_completed:       { label: 'Completed Order',       color: '#0A6629', bgColor: '#CBEAD5' },
  order_processing:      { label: 'Processing Order',      color: '#E0961E', bgColor: '#FCEACC' },
  order_canceled_order:  { label: 'Canceled Order',        color: '#FF1010', bgColor: '#F7D8D8' },
  order_redraw_order:    { label: 'Redraw Order',          color: '#FF1010', bgColor: '#F7D8D8' },
  order_pending_payment: { label: 'Pending Payment Order', color: '#E0961E', bgColor: '#FCEACC' },
  order_modified:        { label: 'Modified Order',        color: '#eb6161', bgColor: '#ead3d3' },
};

const PAYMENT_INFO_MAP = {
  customerPay: 'Provider Pay',
  patientPayNow: 'Patient Pay',
  patientPayLater: 'Patient Pay (Later - send email/text)',
};

const formatOrderDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: '2-digit', day: '2-digit', year: 'numeric',
  }).replace(/\//g, '-');
};

/**
 * Fetch order details for rendering an order card.
 * Called by CardActionNode on note load to refresh order info.
 *
 * @param {string} accessionId - The accession/barcode ID (card id)
 * @param {string} sampleId - The sample ID
 * @returns {Promise<{description: string, orderStatus: string, orderData: object}|null>}
 */
export const fetchOrderCardDetails = async (accessionId, sampleId) => {
  if (!accessionId || !sampleId) return null;

  const userInfo = store.state.login?.userInfo;
  // patientId from the current route (uses history mode, not hash mode)
  const patientId = window.location.pathname?.match(/encounter-notes\/([^/]+)\//)?.[1] || '';

  try {
    const { data } = await transSericeApolloClient.query({
      query: GET_PATIENT_ORDER,
      variables: {
        patientId: String(patientId),
        accessionId: String(accessionId),
        sampleId: String(sampleId),
        customerId: String(userInfo?.customer_id || ''),
        clinicId: String(userInfo?.clinic_id || ''),
      },
      fetchPolicy: 'network-only',
      context: { _silent: true },
    });

    const order = data?.patientProfile?.order?.data;
    if (!order) return null;

    // Get latest status from order_time_line (last entry is most recent)
    const timeline = order.order_time_line || [];
    const rawStatus = timeline.length > 0
      ? timeline[timeline.length - 1].status
      : (order.order_status || 'order_received');
    const statusConfig = ORDER_STATUS_CONFIG[rawStatus] || ORDER_STATUS_CONFIG.order_received;

    const parts = [];
    parts.push(`Order Placed On: ${formatOrderDate(order.service_date)}`);
    parts.push(`Amount: $${order.total ?? order.actual_price ?? '0.00'}`);
    const payMethod = PAYMENT_INFO_MAP[order.payment_info] || order.payment_info || 'N/A';
    parts.push(`Payment Method: ${payMethod}`);

    return {
      description: parts.join(' | '),
      orderStatus: rawStatus,
      statusLabel: statusConfig.label,
      statusColor: statusConfig.color,
      statusBgColor: statusConfig.bgColor,
      sample_id: order.sample_id || sampleId,
      orderData: order,
    };
  } catch (err) {
    console.error('Failed to fetch order details:', err);
    return null;
  }
};

/**
 * Check the status of an action card (task, appointment, or document).
 * Returns { isDeleted, data } where:
 *   - isDeleted: true if the entity no longer exists or has been cancelled
 *   - data: the fetched entity details (task/event/file metadata), or null
 *
 * Used by both RichEditor.vue (live editor) and generateNotePdf.js (PDF export)
 * to determine whether a card should be shown as deleted/cancelled.
 *
 * @param {string} cardType - One of CARD_TYPES (task, appointment, document)
 * @param {string|number} cardId - The entity ID
 * @param {string|number} [calendarId] - Calendar ID (only used for appointments)
 * @returns {Promise<{isDeleted: boolean, data: object|null}>}
 */
export const checkCardStatus = async (cardType, cardId, calendarId) => {
  if (!cardId) return { isDeleted: false, data: null };

  try {
    if (cardType === CARD_TYPES.APPOINTMENT) {
      const event = await getEventById(cardId, calendarId);
      if (!event || event.is_canceled) {
        return { isDeleted: true, data: event };
      }
      return { isDeleted: false, data: event };
    }

    if (cardType === CARD_TYPES.TASK) {
      const task = await getTaskById(cardId);
      if (!task || task._notFound || task.is_active === false) {
        return { isDeleted: true, data: task };
      }
      return { isDeleted: false, data: task };
    }

    if (cardType === CARD_TYPES.DOCUMENT) {
      try {
        // Use metadata endpoint (JSON) instead of /files/decrypt (blob) for lightweight check
        const response = await transServiceV2({ url: `/files/${cardId}`, method: 'get', _silent: true });
        return { isDeleted: false, data: response?.data || response };
      } catch (error) {
        if (error.response && (error.response.status === 403 || error.response.status === 404)) {
          return { isDeleted: true, data: null };
        }
        return { isDeleted: false, data: null };
      }
    }
  } catch (e) {
    console.error(`Failed to check status for ${cardType} card:`, e);
  }

  return { isDeleted: false, data: null };
};

/**
 * Fetch form assignment submission status for rendering a form card.
 * Called by CardActionNode on note reload.
 * API response: { assignment_id, last_submitted_at }
 *
 * @param {string} assignmentId - The form assignment ID (card id)
 * @returns {Promise<{isCompleted: boolean}|null>}
 */
export const fetchFormCardStatus = async (assignmentId) => {
  if (!assignmentId) return null;
  try {
    const { getFormSubmissionStatusApi } = await import('@/api/documents');
    const response = await getFormSubmissionStatusApi(assignmentId);
    const data = response?.data;
    if (!data) return null;
    return {
      isCompleted: data.last_submitted_at != null,
    };
  } catch (err) {
    console.error('Failed to fetch form submission status:', err);
    return null;
  }
};
